/** Las evaluaciones del desempeño (`/api/v1/evaluaciones`, `services/evaluaciones.py`). */

export type Criterio = { codigo: string; nombre: string; descripcion: string }
export type Bloque = { clave: string; nombre: string; peso: string; criterios: Criterio[] }
export type Formulario = {
  clave: string
  nombre: string
  bloques: Bloque[]
  indicadores: { texto: string; fuente: string }[]
}

export type FilaBloque = {
  clave: string
  nombre: string
  peso: string
  auto: string | null
  direccion: string | null
  brecha: string | null
  acordada: string | null
  ponderada: string | null
  revisar: boolean
}

export type Resultado = {
  bloques: FilaBloque[]
  final: string | null
  nivel: string | null
  consecuencia: string | null
  tope_etica: boolean
  faltan: string[]
}

/** Criterio → nota 1..5; `null` = N/O. Sin la clave: todavía sin calificar. */
export type Notas = Record<string, number | null>

export type CompromisoTrabajador = { que: string; accion: string; medida: string; fecha: string }
export type CompromisoEmpresa = { compromiso: string; responsable: string; fecha: string }
export type Pdi = {
  trabajador?: CompromisoTrabajador[]
  empresa?: CompromisoEmpresa[]
  decision?: string
  seguimiento?: string
}

export type EvaluacionResumen = {
  id: number
  persona_id: number
  persona: string
  formulario: string
  desde: string
  hasta: string
  estado: "ABIERTA" | "CONSOLIDADA" | "CERRADA" | "ANULADA"
  auto_enviada_en: string | null
  final: string | null
  nivel: string | null
}

export type Evaluacion = EvaluacionResumen & {
  notas_auto: Notas
  notas_direccion: Notas
  hechos: Record<string, string>
  autoevaluacion: Record<string, string>
  indicadores: Record<string, { resultado?: string; comentario?: string }>
  acordadas: Record<string, number | null>
  valoracion: { fortalezas?: string; mejoras?: string; brechas?: string }
  pdi: Pdi
  observaciones_trabajador: string | null
  cerrada_en: string | null
  resultado: Resultado | null
  plantilla: Formulario
}

export const ESTADO: Record<EvaluacionResumen["estado"], string> = {
  ABIERTA: "Abierta: se califica",
  CONSOLIDADA: "Consolidada: notas acordadas",
  CERRADA: "Cerrada: PDI firmado",
  ANULADA: "Anulada",
}

/** La escala del programa (guía §4). */
export const ESCALA: { valor: string; texto: string }[] = [
  { valor: "", texto: "—" },
  { valor: "5", texto: "5 · Sobresaliente" },
  { valor: "4", texto: "4 · Muy bueno" },
  { valor: "3", texto: "3 · Cumple" },
  { valor: "2", texto: "2 · En desarrollo" },
  { valor: "1", texto: "1 · Insuficiente" },
  { valor: "NO", texto: "N/O · No observado" },
]

export const DECISIONES = [
  "Reconocimiento",
  "Mantener y desarrollar",
  "Plan de mejora con seguimiento a 45 días",
  "Revisión de funciones o del cargo",
]

/** El valor del selector para una nota guardada. */
export function valorDe(notas: Notas, codigo: string): string {
  if (!(codigo in notas)) return ""
  const n = notas[codigo]
  return n === null || n === undefined ? "NO" : String(n)
}

/** Lo que queda en las notas al elegir en el selector: vacío borra la clave. */
export function conNota(notas: Notas, codigo: string, valor: string): Notas {
  const nuevas = { ...notas }
  if (valor === "") delete nuevas[codigo]
  else nuevas[codigo] = valor === "NO" ? null : Number(valor)
  return nuevas
}

/** «3,93» con coma decimal. */
export const nota = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : Number(v).toFixed(2).replace(".", ",")
